/**
 * Error Reporting Service
 * Sends frontend errors to backend audit system
 */

import axios, { AxiosInstance } from 'axios';

// Create a separate axios instance for error reporting to avoid circular dependencies
const errorReportingClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface ErrorReport {
  error_type: string;
  error_message: string;
  stack_trace?: string;
  endpoint?: string;
  component?: string;
  user_agent?: string;
  context?: Record<string, any>;
}

/**
 * Report error to backend audit system
 */
export const reportError = async (error: Error | string, context?: {
  component?: string;
  endpoint?: string;
  additionalContext?: Record<string, any>;
}): Promise<void> => {
  try {
    const errorReport: ErrorReport = {
      error_type: typeof error === 'string' ? 'FrontendError' : error.constructor.name,
      error_message: typeof error === 'string' ? error : error.message,
      stack_trace: typeof error === 'string' ? undefined : error.stack,
      component: context?.component,
      endpoint: context?.endpoint || window.location.pathname,
      user_agent: navigator.userAgent,
      context: {
        url: window.location.href,
        timestamp: new Date().toISOString(),
        ...context?.additionalContext
      }
    };

    // Send to backend audit endpoint
    await errorReportingClient.post('/audit/frontend-error', errorReport);
  } catch (reportingError) {
    // Don't let error reporting itself break the app
    console.error('Failed to report error to audit system:', reportingError);
  }
};

/**
 * Report network error with request/response details
 */
export const reportNetworkError = async (error: any, config?: {
  url?: string;
  method?: string;
  requestData?: any;
  responseData?: any;
  statusCode?: number;
}): Promise<void> => {
  try {
    const errorReport: ErrorReport = {
      error_type: 'NetworkError',
      error_message: error.message || 'Network request failed',
      stack_trace: error.stack,
      endpoint: config?.url || window.location.pathname,
      component: 'NetworkLayer',
      user_agent: navigator.userAgent,
      context: {
        url: window.location.href,
        timestamp: new Date().toISOString(),
        method: config?.method,
        requestData: config?.requestData,
        responseData: config?.responseData,
        statusCode: config?.statusCode
      }
    };

    await errorReportingClient.post('/audit/frontend-error', errorReport);
  } catch (reportingError) {
    console.error('Failed to report network error to audit system:', reportingError);
  }
};

/**
 * Setup global error handlers
 */
export const setupGlobalErrorHandlers = (): void => {
  // Catch unhandled errors
  window.onerror = (message, source, lineno, colno, error) => {
    reportError(error || new Error(String(message)), {
      component: 'GlobalErrorHandler',
      additionalContext: {
        source,
        lineno,
        colno
      }
    });
    return false; // Let default error handling continue
  };

  // Catch unhandled promise rejections
  window.onunhandledrejection = (event) => {
    reportError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      {
        component: 'UnhandledPromiseRejection',
        additionalContext: {
          promise: event.promise
        }
      }
    );
  };

  // Intercept console.error to capture additional errors
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    // Call original console.error
    originalConsoleError.apply(console, args);

    // Report to audit system
    const errorMessage = args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');

    reportError(errorMessage, {
      component: 'ConsoleError',
      additionalContext: {
        arguments: args
      }
    });
  };

  console.log('Global error handlers initialized');
};
